import { forwardRef, type HTMLAttributes } from "react";
import { Chip } from "./chip.js";
import { cn } from "./lib/cn.js";
import { actionToProse, type ActionLike } from "./lib/action-prose.js";

/**
 * Display unit for one model's per-turn reasoning + the action it
 * submitted. Stack these chronologically inside a `<DecisionTrace>`
 * column for a single-side view, or render two columns side-by-side
 * for A-vs-B comparison.
 *
 * Reasoning prose is rendered verbatim — no truncation. The action
 * lines render in mono with plain-English tool names via
 * `actionToProse()`.
 */

export interface DecisionTurn {
 turn: number;
 /** "A" / "B" — used for the side chip and aria label. */
 side: "A" | "B";
 /** The model that produced this decision, e.g. "protolabs/fast". */
 model?: string;
 reasoning?: string;
 action?: ActionLike | null;
 /** Optional token telemetry; renders a `[in / out]` chip when present. */
 tokensIn?: number;
 tokensOut?: number;
}

export interface DecisionTraceProps extends HTMLAttributes<HTMLDivElement> {
 turns: DecisionTurn[];
 /** Highlight a specific turn (e.g. the one currently visible in the renderer). */
 activeTurn?: number;
 /** "stacked" (single column, A and B interleaved) or "split" (A left, B right). */
 layout?: "stacked" | "split";
 /** Optional empty message when turns is empty. */
 emptyMessage?: string;
}

const sideTone = (side: "A" | "B") => (side === "A" ? "accent" : "default");

function TurnCard({ entry, active }: { entry: DecisionTurn; active: boolean }) {
 const prose = actionToProse(entry.action);
 return (
 <article
 aria-label={`Side ${entry.side} turn ${entry.turn}`}
 className={cn(
 "flex flex-col gap-2 p-3 ",
 "border border-[var(--pp-border)]",
 active ? "bg-[var(--pp-bg-strong)]" : "bg-[transparent]",
 )}
 style={{ transition: "background-color var(--duration-fast, 120ms)" }}
 >
 <header style={{ display: "flex", alignItems: "center", gap: 8 }}>
 <Chip tone={sideTone(entry.side)} dot={active}>
 T{String(entry.turn).padStart(2, "0")} · {entry.side}
 </Chip>
 {entry.model && (
 <span
 style={{
 fontFamily: "var(--pp-font-family)",
 fontSize: "var(--pp-font-sm)",
 color: "var(--pp-text-muted)",
 }}
 >
 {entry.model}
 </span>
 )}
 {(entry.tokensIn !== undefined || entry.tokensOut !== undefined) && (
 <Chip tone="muted" style={{ marginLeft: "auto" }}>
 {entry.tokensIn ?? "?"} in / {entry.tokensOut ?? "?"} out
 </Chip>
 )}
 </header>

 {entry.reasoning ? (
 <p
 style={{
 margin: 0,
 color: "var(--pp-text-muted)",
 fontSize: "var(--pp-font-md)",
 lineHeight: 1.55,
 }}
 >
 {entry.reasoning}
 </p>
 ) : (
 <p
 style={{
 margin: 0,
 color: "var(--pp-text-muted)",
 fontSize: "var(--pp-font-sm)",
 fontFamily: "var(--pp-font-family)",
 }}
 >
 (no reasoning — heuristic agent)
 </p>
 )}

 {prose.length > 0 ? (
 <ul
 style={{
 listStyle: "none",
 padding: 0,
 margin: 0,
 display: "flex",
 flexDirection: "column",
 gap: 2,
 fontFamily: "var(--pp-font-family)",
 fontSize: "var(--pp-font-sm)",
 color: "var(--pp-text)",
 letterSpacing: "0.01em",
 }}
 >
 {prose.map((line, i) => (
 <li key={i}>
 <span style={{ color: "var(--pp-text-muted)" }}>▸ </span>
 {line}
 </li>
 ))}
 </ul>
 ) : (
 <span
 style={{
 color: "var(--pp-text-muted)",
 fontSize: "var(--pp-font-sm)",
 fontFamily: "var(--pp-font-family)",
 }}
 >
 (no action submitted)
 </span>
 )}
 </article>
 );
}

export const DecisionTrace = forwardRef<HTMLDivElement, DecisionTraceProps>(
 (
 {
 turns,
 activeTurn,
 layout = "stacked",
 emptyMessage,
 className,
 ...props
 },
 ref,
 ) => {
 if (turns.length === 0) {
 return (
 <div
 ref={ref}
 className={cn(
 "py-8 text-center text-[var(--pp-text-muted)] border border-dashed border-[var(--pp-border)] ",
 className,
 )}
 {...props}
 >
 {emptyMessage ?? "No decisions recorded — heuristic-only match or transcripts disabled."}
 </div>
 );
 }

 if (layout === "split") {
 const aTurns = turns.filter((t) => t.side === "A").sort((a, b) => a.turn - b.turn);
 const bTurns = turns.filter((t) => t.side === "B").sort((a, b) => a.turn - b.turn);
 return (
 <div
 ref={ref}
 className={cn("grid grid-cols-2 gap-3", className)}
 {...props}
 >
 <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
 {aTurns.map((t) => (
 <TurnCard key={`a-${t.turn}`} entry={t} active={t.turn === activeTurn} />
 ))}
 {aTurns.length === 0 && (
 <p style={{ color: "var(--pp-text-muted)", fontSize: "var(--pp-font-sm)" }}>
 No A-side decisions.
 </p>
 )}
 </div>
 <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
 {bTurns.map((t) => (
 <TurnCard key={`b-${t.turn}`} entry={t} active={t.turn === activeTurn} />
 ))}
 {bTurns.length === 0 && (
 <p style={{ color: "var(--pp-text-muted)", fontSize: "var(--pp-font-sm)" }}>
 No B-side decisions.
 </p>
 )}
 </div>
 </div>
 );
 }

 // stacked: chronological order, both sides interleaved.
 const sorted = [...turns].sort((a, b) =>
 a.turn === b.turn ? a.side.localeCompare(b.side) : a.turn - b.turn,
 );
 return (
 <div
 ref={ref}
 className={cn("flex flex-col gap-2", className)}
 {...props}
 >
 {sorted.map((t) => (
 <TurnCard key={`${t.side}-${t.turn}`} entry={t} active={t.turn === activeTurn} />
 ))}
 </div>
 );
 },
);

DecisionTrace.displayName = "DecisionTrace";
